import { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../utils/apiClient';
import { KNOWLEDGE_HUB_SLUG } from '../config';
import LoadingBar from '../components/ui/LoadingBar';
import { setSentryUser } from '../utils/sentry';
import { setNewRelicUser } from '../utils/newrelic';
import { useAuth } from './AuthContext';
import { hubAppCopyContext, appCopyContext } from '../contexts';


const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  // Get user from AuthContext instead of maintaining separate auth state
  // AppProvider MUST be rendered inside AuthProvider (as it is in App.tsx)
  const authContext = useAuth();
  const authUser = authContext?.user || null;
  const isAuthenticated = authContext?.isAuthenticated || false;
  
  const [state, setState] = useState({
    user: null, // This will be synced with AuthContext
    isAuthenticated: false, // This will be synced with AuthContext
    locale: 'en-US',
    appCopy: {},
    hubAppCopy: {},
    programs: [],
    userPrograms: [],
    userApps: [],
    products: [],
    userProducts: [],
    relatedFiles: [],
    currentHubToolSlug: KNOWLEDGE_HUB_SLUG, // Default to knowledge hub
    expandedSideNav: false,
    expandedNavItems: [],
    isLoading: true,
  });

  // Sync user from AuthContext to AppContext
  useEffect(() => {
    if (authUser) {
      setState(prev => ({
        ...prev,
        user: authUser,
        isAuthenticated: true,
      }));
    } else if (!authContext?.isLoading) {
      setState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false,
      }));
    }
  }, [authUser, authContext?.isLoading]);

  // Load initial data when user is available from AuthContext
  useEffect(() => {
    // Only load data if we have an authenticated user from AuthContext
    // AuthContext handles both Auth0 users and QA users consistently
    if (authUser) {
      loadInitialData();
    } else if (!authContext?.isLoading) {
      // Load minimal data (app copy only) for public routes
      loadPublicData();
    }
  }, [authUser, authContext?.isLoading]);

  // Watch for user locale changes and reload app copy accordingly
  useEffect(() => {
    const currentUserLocale = authUser?.preferredLocale || 'en-US';
    
    // Only reload if locale changed and we already have loaded data initially
    if (!state.isLoading && state.locale !== currentUserLocale && authUser) {
      reloadAppCopy(currentUserLocale);
    }
  }, [authUser?.preferredLocale, state.isLoading, state.locale, authUser]);


  const loadPublicData = async (locale = 'en-US') => {
    try {
      // Load only app copy for public routes (no user-specific data)
      const [appCopyResponse, hubAppCopyResponse] = await Promise.all([
        api.getAppCopy(locale).catch(err => {
          console.error('Failed to fetch app copy:', err);
          return null;
        }),
        api.getHubAppCopy(locale).catch(err => {
          console.error('Failed to fetch hub app copy:', err);
          return null;
        }),
      ]);


      // Helper function to convert snake_case to camelCase
      const toCamelCase = (str) => {
        return str.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
      };

      // Helper function to transform keys in an object from snake_case to camelCase
      const transformKeys = (obj) => {
        if (Array.isArray(obj)) {
          return obj.map(transformKeys);
        } else if (obj !== null && typeof obj === 'object') {
          return Object.keys(obj).reduce((result, key) => {
            const camelKey = toCamelCase(key);
            result[camelKey] = transformKeys(obj[key]);
            return result;
          }, {});
        }
        return obj;
      };

      // Parse app copy - the API returns flattened fields at the root level
      const appCopyRaw = appCopyResponse?.data?.data;
      const appCopyData = appCopyRaw ? (() => {
        const { id, sys, environment, content_type, revision, created_at, updated_at, published_at, first_published_at, published_version, locale, ...fields } = appCopyRaw;
        return transformKeys(fields);
      })() : {};

      const hubAppCopyRaw = hubAppCopyResponse?.data?.data;
      const hubAppCopyData = hubAppCopyRaw ? (() => {
        const { id, sys, environment, content_type, revision, created_at, updated_at, published_at, first_published_at, published_version, locale, ...fields } = hubAppCopyRaw;
        return transformKeys(fields);
      })() : {};


      setState(prev => ({
        ...prev,
        appCopy: appCopyData,
        hubAppCopy: hubAppCopyData,
        isLoading: false,
      }));

    } catch (error) {
      console.error('Failed to load public data:', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const loadInitialData = async () => {
    try {
      // Use authenticated user from AuthContext (handles both Auth0 and QA users)
      const currentUser = authUser;
      const userId = currentUser?.id;
      const userLocale = currentUser?.preferredLocale || 'en-US';

      // Try to fetch real data from API first using user's preferred locale
      const [appCopyResponse, hubAppCopyResponse, relatedFilesResponse, programsResponse, userAppsResponse] = await Promise.all([
        api.getAppCopy(userLocale).catch(err => {
          console.error('Failed to fetch app copy:', err);
          return null;
        }),
        api.getHubAppCopy(userLocale).catch(err => {
          console.error('Failed to fetch hub app copy:', err);
          return null;
        }),
        userId ? api.getRelatedFiles(userId).catch(err => {
          console.error('Failed to fetch related files:', err);
          return null;
        }) : null,
        userId ? api.getPrograms('all', userLocale).catch(err => {
          console.error('Failed to fetch user programs:', err);
          return null;
        }) : null,
        userId ? api.getUserApps().catch(err => {
          console.error('Failed to fetch user apps:', err);
          return null;
        }) : null,
      ]);

      // Helper function to convert snake_case to camelCase
      const toCamelCase = (str) => {
        return str.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
      };

      // Helper function to transform keys in an object from snake_case to camelCase
      const transformKeys = (obj) => {
        if (Array.isArray(obj)) {
          return obj.map(transformKeys);
        } else if (obj !== null && typeof obj === 'object') {
          return Object.keys(obj).reduce((result, key) => {
            const camelKey = toCamelCase(key);
            result[camelKey] = transformKeys(obj[key]);
            return result;
          }, {});
        }
        return obj;
      };

      // The API returns flattened fields at the root level
      const appCopyRaw = appCopyResponse?.data?.data;
      const appCopyData = appCopyRaw ? (() => {
        const { id, sys, environment, content_type, revision, created_at, updated_at, published_at, first_published_at, published_version, locale, ...fields } = appCopyRaw;
        return transformKeys(fields);
      })() : {};

      const hubAppCopyRaw = hubAppCopyResponse?.data?.data;
      const hubAppCopyData = hubAppCopyRaw ? (() => {
        const { id, sys, environment, content_type, revision, created_at, updated_at, published_at, first_published_at, published_version, locale, ...fields } = hubAppCopyRaw;
        return transformKeys(fields);
      })() : {};

      // Extract items from Contentful response format
      const programsData = programsResponse?.data?.data?.items || []

      // Extract user apps from API response
      const userAppsData = userAppsResponse?.data?.data || []

      // Use the authenticated user from AuthContext (already properly formatted)
      const userData = currentUser;

      // Parse related files if available (URLs are rewritten at render time via rewriteUrl)
      const relatedFilesData = relatedFilesResponse?.data?.data || [];

      // Set Sentry and NewRelic user context
      setSentryUser(userData);
      setNewRelicUser(userData);
            
      setState(prev => ({
        ...prev,
        user: userData,
        isAuthenticated: isAuthenticated,
        programs: [],
        userPrograms: programsData,
        userApps: userAppsData,
        products: [],
        userProducts: [],
        relatedFiles: relatedFilesData,
        appCopy: appCopyData,
        hubAppCopy: hubAppCopyData,
        locale: userLocale,
        isLoading: false,
      }));

    } catch (error) {
      console.error('Error loading initial data:', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const updateUser = (user) => {
    // Update Sentry and NewRelic user context when user changes
    if (user) {
      setSentryUser(user);
      setNewRelicUser(user);
    }
    
    setState(prev => ({
      ...prev,
      user,
      isAuthenticated: !!user
    }));
  };

  const setLocale = (locale) => {
    setState(prev => ({ ...prev, locale }));
  };

  const reloadAppCopy = async (locale) => {
    try {
      const [appCopyResponse, hubAppCopyResponse] = await Promise.all([
        api.getAppCopy(locale).catch(err => {
          console.error('Failed to fetch app copy:', err);
          return null;
        }),
        api.getHubAppCopy(locale).catch(err => {
          console.error('Failed to fetch hub app copy:', err);
          return null;
        }),
      ]);

      // Helper function to convert snake_case to camelCase
      const toCamelCase = (str) => {
        return str.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
      };

      // Helper function to transform keys in an object from snake_case to camelCase
      const transformKeys = (obj) => {
        if (Array.isArray(obj)) {
          return obj.map(transformKeys);
        } else if (obj !== null && typeof obj === 'object') {
          return Object.keys(obj).reduce((result, key) => {
            const camelKey = toCamelCase(key);
            result[camelKey] = transformKeys(obj[key]);
            return result;
          }, {});
        }
        return obj;
      };

      // Parse app copy - the API returns flattened fields at the root level
      const appCopyRaw = appCopyResponse?.data?.data;
      const appCopyData = appCopyRaw ? (() => {
        const { id, sys, environment, content_type, revision, created_at, updated_at, published_at, first_published_at, published_version, locale, ...fields } = appCopyRaw;
        return transformKeys(fields);
      })() : {};

      const hubAppCopyRaw = hubAppCopyResponse?.data?.data;
      const hubAppCopyData = hubAppCopyRaw ? (() => {
        const { id, sys, environment, content_type, revision, created_at, updated_at, published_at, first_published_at, published_version, locale, ...fields } = hubAppCopyRaw;
        return transformKeys(fields);
      })() : {};

      const programsData = programsResponse?.data?.data?.items || []

      setState(prev => ({
        ...prev,
        appCopy: appCopyData,
        hubAppCopy: hubAppCopyData,
        userPrograms: programsData,
        locale,
      }));

    } catch (error) {
      console.error('Failed to reload app copy:', error);
    }
  };

  const setExpandedSideNav = (expanded) => {
    setState(prev => ({ ...prev, expandedSideNav: expanded }));
  };

  const setExpandedNavItems = (items) => {
    setState(prev => ({ ...prev, expandedNavItems: items }));
  };

  const value = {
    ...state,
    updateUser,
    setLocale,
    reloadAppCopy,
    setExpandedSideNav,
    setExpandedNavItems,
  };

  // Show only loading bar until everything is ready
  if (state.isLoading) {
    return <LoadingBar />;
  }

  // Provide hubAppCopyContext at App level so all components (including modals
  // rendered outside ApplicationLayout) can use the useHubAppCopy() hook
  return (
    <AppContext.Provider value={value}>
      <hubAppCopyContext.Provider value={state.hubAppCopy}>
        <appCopyContext.Provider value={state.appCopy}>
          {children}
        </appCopyContext.Provider>
      </hubAppCopyContext.Provider>
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const useAppContext = useApp; // Alternative export name

export default AppContext;